/* The counter's offline queue.
 *
 * WHAT THIS REPLACES
 * ==================
 * The counter caught `Offline`, said "Saved. We will confirm this shortly.",
 * and threw the stamp away. A named person, standing at a till, was told their
 * purchase had been recorded when nothing had been. That copy is gone and the
 * counter has been telling the truth since — "Not saved. There is no
 * connection." — which is a worse product and an honest one. This is the thing
 * that makes it a better product without making it a liar again.
 *
 * THE BOUND IS THE FRAUD BOUND, AND IT IS THE SERVER'S
 * ====================================================
 * Forty entries and six hours, from `merchant_settings`. The server enforces
 * both in `sync_offline_queue` and writes an `E_OFFLINE_LIMIT` refusal to the
 * ledger for anything over: an entry that vanishes with no record is the one
 * outcome provisional-accept rules out. The numbers here are a copy for the
 * countdown, never the control. A client that could raise its own ceiling
 * would be the whole fraud surface.
 *
 * WHAT IS NEVER QUEUED
 * ====================
 * Redemptions. There is no offline path for giving something away, and the
 * server has no way to express one: `/counter/redeem` takes no `source`. That
 * is the rule made structural rather than checked.
 *
 * WHY A STORE INTERFACE
 * =====================
 * Two implementations: IndexedDB for the till, and an in-memory one for tests.
 * The interface is not for flexibility — nobody is going to want a third — it
 * is so the drain logic can be tested without a browser, while the IndexedDB
 * implementation is tested against a real IndexedDB. Testing the drain against
 * a fake and calling the storage proven would be the instrument measuring
 * itself. */

/** One held stamp. Exactly the shape `/counter/sync` accepts, so nothing is
 * translated on the way out and there is no second definition to drift. */
export interface HeldEntry {
  /** Minted when the stamp is taken, and reused for every send attempt of that
   * stamp forever. This is what makes a retry safe: the server has seen this
   * key or it has not. */
  idempotency_key: string;
  code: string;
  /** When it happened at the counter, NOT when it reached us. The minimum-gap
   * rule is judged on this, and a queue replayed with arrival times would
   * refuse stamps that were minutes apart in the shop. */
  occurred_at: string;
  branch_id?: string | null;
  trigger?: string;
}

export interface QueueStore {
  add(entry: HeldEntry): Promise<void>;
  /** Oldest first. The server sorts too, and does not trust this — but sending
   * in order means the outcomes come back in the order a person would expect
   * to read them. */
  all(): Promise<HeldEntry[]>;
  remove(keys: string[]): Promise<void>;
  count(): Promise<number>;
  clear(): Promise<void>;
}

const DB_NAME = "damga-counter";
const DB_VERSION = 1;
const STORE = "held";

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        // Keyed by idempotency_key, so adding the same stamp twice is one
        // stamp. The keypath does the deduplication that a retry loop would
        // otherwise have to remember to do.
        db.createObjectStore(STORE, { keyPath: "idempotency_key" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function promisify<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** The till's queue. Survives a reload, a crash, and the tab being closed —
 * which is the whole reason it is not in memory. */
export function indexedDbQueue(): QueueStore {
  async function tx(mode: IDBTransactionMode) {
    const db = await open();
    return db.transaction(STORE, mode).objectStore(STORE);
  }
  return {
    async add(entry) {
      await promisify((await tx("readwrite")).put(entry));
    },
    async all() {
      const rows = (await promisify((await tx("readonly")).getAll())) as HeldEntry[];
      return rows.sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
    },
    async remove(keys) {
      const store = await tx("readwrite");
      await Promise.all(keys.map((k) => promisify(store.delete(k))));
    },
    async count() {
      return promisify((await tx("readonly")).count());
    },
    async clear() {
      await promisify((await tx("readwrite")).clear());
    },
  };
}

/** For tests, and for a browser that has no IndexedDB at all.
 *
 * A till running on this loses its queue when the tab closes, which is worse
 * than the disk-backed one and much better than discarding the stamp at the
 * moment it is taken. The caller is told which it got, so the interface can
 * say so. */
export function memoryQueue(): QueueStore {
  const rows = new Map<string, HeldEntry>();
  return {
    async add(entry) {
      rows.set(entry.idempotency_key, entry);
    },
    async all() {
      return [...rows.values()].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
    },
    async remove(keys) {
      for (const k of keys) rows.delete(k);
    },
    async count() {
      return rows.size;
    },
    async clear() {
      rows.clear();
    },
  };
}

/** IndexedDB when it is there, memory when it is not.
 *
 * Private browsing and some locked-down configurations throw on `open` rather
 * than returning null, and a till that cannot open a database must still take
 * stamps. */
export function createQueue(): { store: QueueStore; durable: boolean } {
  try {
    if (typeof indexedDB !== "undefined") return { store: indexedDbQueue(), durable: true };
  } catch {
    /* fall through */
  }
  return { store: memoryQueue(), durable: false };
}

// --- the ceiling, as the counter sees it ------------------------------------

export interface QueueLimits {
  /** merchant_settings.offline_queue_max_entries. */
  maxEntries: number;
  /** merchant_settings.offline_queue_max_hours. */
  maxHours: number;
}

export const DEFAULT_LIMITS: QueueLimits = { maxEntries: 40, maxHours: 6 };

/** Milliseconds until the OLDEST held stamp passes the age ceiling.
 *
 * The oldest, not the newest: the queue dies from the front. A till that has
 * been offline five hours and takes one more stamp has an hour left, not six,
 * and a countdown reading six would be the confident wrong number that this
 * whole file exists to avoid. */
export function msUntilExpiry(
  entries: HeldEntry[],
  now: Date,
  limits: QueueLimits = DEFAULT_LIMITS,
): number | null {
  if (entries.length === 0) return null;
  const oldest = entries.reduce((a, b) => (a.occurred_at <= b.occurred_at ? a : b));
  const deadline = Date.parse(oldest.occurred_at) + limits.maxHours * 3_600_000;
  return Math.max(0, deadline - now.getTime());
}

/** How full, as a fraction, by whichever bound is closer.
 *
 * Two ceilings and one indicator: a till at 39 of 40 entries and a till with
 * ten minutes left are equally close to losing stamps, and a person reading
 * the counter needs one number that means "how bad is this". */
export function pressure(
  entries: HeldEntry[],
  now: Date,
  limits: QueueLimits = DEFAULT_LIMITS,
): number {
  if (entries.length === 0) return 0;
  const byCount = entries.length / limits.maxEntries;
  const left = msUntilExpiry(entries, now, limits) ?? 0;
  const byTime = 1 - left / (limits.maxHours * 3_600_000);
  return Math.min(1, Math.max(byCount, byTime));
}

// --- draining ----------------------------------------------------------------

/** What `/counter/sync` answers with, per entry, keyed by idempotency key. */
export interface SyncResponse {
  status?: number;
  synced?: boolean;
  submitted?: number;
  accepted?: number;
  rejected?: number;
  over_limit?: number;
  results?: Record<string, { accepted?: boolean; refusal?: string }>;
  refusal?: string;
}

export interface DrainOutcome {
  /** Nothing to send, or no connection, or the server answered. */
  kind: "empty" | "offline" | "sent" | "refused";
  submitted: number;
  accepted: number;
  /** Refused by a rule — a duplicate, a gap, the ceiling. Resolved, not lost:
   * the server wrote a ledger row for each. */
  refused: number;
  /** Still held after this attempt. */
  remaining: number;
  /** Set when the whole request was refused rather than the entries. */
  refusal?: string;
}

/** Send everything held, once, and delete only what the server answered for.
 *
 * THE DELETION RULE IS THE WHOLE OF THE SAFETY HERE. An entry is removed if
 * and only if the server returned an outcome for its idempotency key —
 * accepted or refused, both are answers. Anything the server did not mention
 * stays held, so a partial response, a truncated body or a connection that
 * dies mid-reply costs nothing. Deleting on "the request finished" instead
 * would throw away stamps on any of those.
 *
 * Refused entries are deleted too, and that is deliberate: the server has
 * written a ledger row for each refusal, so the record exists where records
 * belong. Keeping them on the device would mean retrying forever something
 * that will be refused identically forever. */
export async function drainQueue(
  post: (body: unknown) => Promise<SyncResponse>,
  store: QueueStore,
  businessId: string,
  deviceId: string | null,
  isOnline: () => boolean = () => true,
): Promise<DrainOutcome> {
  const held = await store.all();
  if (held.length === 0) return { kind: "empty", submitted: 0, accepted: 0, refused: 0, remaining: 0 };

  // A till with no device identity cannot sync: /counter/sync requires a
  // device_id. It keeps holding rather than dropping, and registration retries
  // on the next load.
  if (!deviceId || !isOnline()) {
    return { kind: "offline", submitted: 0, accepted: 0, refused: 0, remaining: held.length };
  }

  let response: SyncResponse;
  try {
    response = await post({ business_id: businessId, device_id: deviceId, entries: held });
  } catch {
    return { kind: "offline", submitted: 0, accepted: 0, refused: 0, remaining: held.length };
  }

  // The whole request refused — not authorised, no such programme. Nothing is
  // deleted: the entries are fine and the caller is not.
  if (response.refusal || (response.status ?? 200) >= 400) {
    return {
      kind: "refused",
      submitted: held.length,
      accepted: 0,
      refused: 0,
      remaining: held.length,
      refusal: response.refusal,
    };
  }

  const results = response.results ?? {};
  const answered = held.filter((e) => e.idempotency_key in results);
  await store.remove(answered.map((e) => e.idempotency_key));

  const accepted = answered.filter((e) => results[e.idempotency_key].accepted === true).length;
  return {
    kind: "sent",
    submitted: held.length,
    accepted,
    refused: answered.length - accepted,
    remaining: await store.count(),
  };
}

// --- the escalation, as ruled ------------------------------------------------
//
// Four stages across the six hours, plus a wall that is not a stage.
//
// RULING 01: whichever is worse. A till at 39 of 40 entries and a till with ten
// minutes left are equally close to losing stamps, and one indicator cannot
// mean two things. `pressure` already takes the max, so a count approaching the
// ceiling reaches the pressing treatment on count alone, which is what warns
// before the wall rather than at it.
//
// RULING 02: never interrupt. No modal at any stage. A dialogue stops the queue
// at the till in the moment the till is most needed, and the shop being busy is
// usually why nobody noticed the connection. The close warning stays the single
// interruption.
//
// RULING 03: the urgent line is merchant-first and sits below the customer's
// lines, never above. It is a placement convention and not a guarantee — the
// customer is reading the same screen. That is acceptable because the line is
// true and specific, and the test for it is not "will they see it" but "is it
// fine if they do".
//
// AND THE FLAT FIRST HALF IS DELIBERATE, WITH A SECOND MECHANISM UNDER IT.
// Nothing changes visually for the first three hours, which would normally be
// a control asleep for half its window. It is not, because the ninety-minute
// till-silence email fires long before any of this: the alert is the control
// early and the till bar is the control late. They are one mechanism seen from
// two ends. Do not trim either as duplication of the other.

export type QueueStage = "empty" | "held" | "accumulating" | "pressing" | "last";

/** Which stage the till is in, from the same numbers the countdown uses. */
export function queueStage(
  entries: HeldEntry[],
  now: Date,
  limits: QueueLimits = DEFAULT_LIMITS,
): QueueStage {
  if (entries.length === 0) return "empty";
  const p = pressure(entries, now, limits);
  if (p >= 0.92) return "last";
  if (p >= 0.75) return "pressing";
  if (p >= 0.5) return "accumulating";
  return "held";
}

/** Is the till at its ceiling and unable to take another stamp?
 *
 * NOT a stage. It can arrive at any point on the clock, including with four
 * hours left, so it is a wall the escalation runs into sideways. And it is
 * probably the common case rather than the edge: forty was chosen as a fraud
 * bound when nothing was queued, and a busy Doha cafe clears forty
 * transactions in well under six hours. */
export function isFull(entries: HeldEntry[], limits: QueueLimits = DEFAULT_LIMITS): boolean {
  return entries.length >= limits.maxEntries;
}

/** Which ceiling is closer: the one that will bite first.
 *
 * Instrumented because the pilot has to answer a question neither of us can
 * reason out — whether the count or the clock is what merchants actually meet.
 * If it is always the count, forty is a number to revisit with the fraud
 * argument reopened deliberately rather than adjusted quietly. */
export function bindingCeiling(
  entries: HeldEntry[],
  now: Date,
  limits: QueueLimits = DEFAULT_LIMITS,
): "count" | "time" | null {
  if (entries.length === 0) return null;
  const byCount = entries.length / limits.maxEntries;
  const left = msUntilExpiry(entries, now, limits) ?? 0;
  const byTime = 1 - left / (limits.maxHours * 3_600_000);
  return byCount >= byTime ? "count" : "time";
}

// --- keeping the tablet awake ------------------------------------------------
//
// WHAT A WAKE LOCK CAN AND CANNOT DO, because the difference decides whether
// the six-hour ceiling is a real bound or an optimistic one.
//
// `navigator.wakeLock.request("screen")` asks the browser to keep the SCREEN
// on. That is all it is. It does not keep the page running when the tab is
// backgrounded, it does not survive the app being switched away from, and
// there is no "system" wake lock in any shipping browser. On iOS the lock is
// released the moment the page is hidden and is not reacquired for you.
//
// So it does not make the queue drain in the background. Nothing does: there
// is no Background Sync on iOS, which is why the close warning exists and why
// the ceiling is six hours rather than a day.
//
// What it DOES buy is the case that actually happens in a shop: a tablet
// propped on a counter, screen dimming to sleep after two minutes, staff not
// touching it because there is nothing to do while the connection is down.
// With the lock the page stays visible, the `online` event fires into a live
// page, and the queue drains the moment the router comes back. Without it the
// tablet sleeps and the drain waits for somebody to pick it up.
//
// It must be reacquired on visibilitychange, because every browser drops it on
// hide and none give it back. That is the part people forget, and the symptom
// is a lock that works until the first time anyone switches app.

export interface WakeLockHandle {
  /** Whether the browser gave us one at all. */
  readonly held: () => boolean;
  release: () => void;
}

/** Hold a screen wake lock while `shouldHold()` is true, reacquiring on
 * visibility changes. Returns a handle whose `release` removes the listeners
 * and drops the lock.
 *
 * Silent on failure by design: a browser without the API, a user who denied
 * it, or a device that refuses under low battery all mean the tablet sleeps a
 * little sooner. None of them is worth a message at a till. */
export function holdScreenAwake(shouldHold: () => boolean): WakeLockHandle {
  type Sentinel = { released: boolean; release: () => Promise<void> };
  let sentinel: Sentinel | null = null;
  let stopped = false;

  const api = (globalThis.navigator as unknown as {
    wakeLock?: { request: (t: string) => Promise<Sentinel> };
  })?.wakeLock;

  async function acquire() {
    if (stopped || sentinel || !api || !shouldHold()) return;
    if (globalThis.document?.visibilityState !== "visible") return;
    try {
      sentinel = await api.request("screen");
      // The browser can take it back at any time — low battery, policy, the
      // user. Forgetting this is how a lock silently stops existing.
      (sentinel as unknown as { onrelease?: () => void }).onrelease = () => {
        sentinel = null;
      };
    } catch {
      sentinel = null;
    }
  }

  function drop() {
    const s = sentinel;
    sentinel = null;
    if (s && !s.released) void s.release().catch(() => {});
  }

  // EVERY browser releases the lock on hide and NONE reacquire it. Without
  // this listener the lock works exactly until the first time somebody
  // switches app, which is the failure that makes people believe wake locks
  // do not work.
  const onVisible = () => {
    if (globalThis.document?.visibilityState === "visible") void acquire();
    else sentinel = null; // already released by the browser
  };
  globalThis.document?.addEventListener("visibilitychange", onVisible);
  void acquire();

  return {
    held: () => sentinel !== null,
    release() {
      stopped = true;
      globalThis.document?.removeEventListener("visibilitychange", onVisible);
      drop();
    },
  };
}
